import { useState, type FormEvent, type RefObject } from "react";

import { Button } from "../components/Button";
import { Checkbox, Field } from "../components/FormControls";
import { Modal } from "../components/Modal";
import { RequestNotice } from "../components/RequestNotice";
import type { BillingAddress, ContactRecord } from "./clientApi";
import { isValidEmailAddress } from "../../shared/email";

const blankAddress = (): BillingAddress => ({ line1: null, line2: null, locality: null, region: null, postalCode: null, country: null });

export function ContactFormModal({ contact, label, onClose, onSubmit, open, restoreFocusRef }: {
  contact?: ContactRecord;
  label: string;
  onClose(): void;
  onSubmit(input: { name: string; email: string | null; phone: string | null; isPrimary: boolean; billingAddress: BillingAddress }): Promise<void>;
  open: boolean;
  restoreFocusRef: RefObject<HTMLElement | null>;
}) {
  const [name, setName] = useState(contact?.name ?? "");
  const [email, setEmail] = useState(contact?.email ?? "");
  const [phone, setPhone] = useState(contact?.phone ?? "");
  const [isPrimary, setIsPrimary] = useState(contact?.isPrimary ?? false);
  const [billingAddress, setBillingAddress] = useState<BillingAddress>(contact?.billingAddress ?? blankAddress());
  const [fieldError, setFieldError] = useState<{ email?: string; name?: string }>({});
  const [requestError, setRequestError] = useState("");
  const [submitting, setSubmitting] = useState(false);

  async function submit(event: FormEvent) {
    event.preventDefault();
    const normalizedName = name.trim();
    const normalizedEmail = email.trim();
    const errors: { email?: string; name?: string } = {};
    if (!normalizedName && !normalizedEmail) errors.email = "Enter a contact name or email address.";
    if (normalizedEmail && !isValidEmailAddress(normalizedEmail)) errors.email = "Enter a valid email address.";
    if (Object.keys(errors).length) {
      setFieldError(errors);
      return;
    }
    setSubmitting(true);
    setRequestError("");
    try {
      await onSubmit({
        name: normalizedName,
        email: normalizedEmail || null,
        phone: phone.trim() || null,
        isPrimary,
        billingAddress,
      });
    } catch (failure) {
      setRequestError(failure instanceof Error ? failure.message : "The contact could not be saved.");
      setSubmitting(false);
    }
  }

  return (
    <Modal open={open} label={label} onClose={() => { if (!submitting) onClose(); }} restoreFocusRef={restoreFocusRef}>
      <form className="modal-form contact-form" onSubmit={submit} noValidate>
        <Field autoFocus label="Contact name" hint="Optional when an email address is provided." maxLength={200} value={name} error={fieldError.name} onChange={(event) => { setName(event.target.value); setFieldError((current) => ({ ...current, name: undefined })); }} />
        <Field label="Email" type="email" maxLength={320} value={email} error={fieldError.email} onChange={(event) => { setEmail(event.target.value); setFieldError((current) => ({ ...current, email: undefined })); }} />
        <Field label="Phone" type="tel" maxLength={50} value={phone} onChange={(event) => setPhone(event.target.value)} />
        <details>
          <summary>Contact address</summary>
          {([ ["line1", "Address line 1", 200], ["line2", "Address line 2", 200], ["locality", "City", 120], ["region", "State or region", 120], ["postalCode", "Postal code", 40], ["country", "Country", 100] ] as const).map(([key, fieldLabel, maximum]) => <Field key={key} label={fieldLabel} maxLength={maximum} value={billingAddress[key] ?? ""} onChange={(event) => setBillingAddress((current) => ({ ...current, [key]: event.target.value }))} />)}
        </details>
        <Checkbox checked={isPrimary} label="Primary contact" onChange={(event) => setIsPrimary(event.target.checked)} />
        {requestError ? <RequestNotice title="Contact not saved" message={requestError} /> : null}
        <div className="modal-actions">
          <Button type="button" variant="quiet" disabled={submitting} onClick={onClose}>Cancel</Button>
          <Button type="submit" loading={submitting}>{contact ? "Save changes" : "Add contact"}</Button>
        </div>
      </form>
    </Modal>
  );
}
